import { useState, useEffect, useContext, useCallback } from 'react'
import * as React from 'react'
import { AppError, errorHandlingService } from '@/shared/services/errorHandlingService'

export type ToastType = 'success' | 'error' | 'warning' | 'info'

export interface Toast {
  id: string
  type: ToastType
  title: string
  message: string
  duration?: number
  actions?: ToastAction[]
  dismissible?: boolean
}

export interface ToastAction {
  label: string
  action: () => void
  style?: 'primary' | 'secondary'
}

interface ToastProps {
  toast: Toast
  onDismiss: (id: string) => void
}

interface ToastProviderProps {
  children: React.ReactNode
}

// Import context from separate file
import { ToastContext } from '@/shared/context/ToastContext'
export { ToastContext }

// Individual toast component
function ToastComponent({ toast, onDismiss }: ToastProps) {
  const [isVisible, setIsVisible] = useState(false)
  const [isLeaving, setIsLeaving] = useState(false)

  const handleDismiss = useCallback(() => {
    setIsLeaving(true)
    setTimeout(() => {
      onDismiss(toast.id)
    }, 300) // Animation duration
  }, [toast.id, onDismiss])

  useEffect(() => {
    // Animate in
    const timer = setTimeout(() => setIsVisible(true), 50)
    return () => clearTimeout(timer)
  }, [])

  useEffect(() => {
    // Auto dismiss
    if (toast.duration && toast.duration > 0) {
      const timer = setTimeout(() => {
        handleDismiss()
      }, toast.duration)
      return () => clearTimeout(timer)
    }
  }, [toast.duration, handleDismiss])

  const getToastStyles = () => {
    const baseStyles = "fixed bottom-4 right-4 max-w-sm w-full bg-white rounded-lg shadow-lg border-l-4 p-4 transform transition-all duration-300 z-50"
    
    const typeStyles = {
      success: "border-green-500",
      error: "border-red-500", 
      warning: "border-yellow-500",
      info: "border-blue-500"
    }

    const animationStyles = isLeaving 
      ? "translate-x-full opacity-0" 
      : isVisible 
        ? "translate-x-0 opacity-100" 
        : "translate-x-full opacity-0"

    return `${baseStyles} ${typeStyles[toast.type]} ${animationStyles}`
  }

  const getIcon = () => {
    const iconStyles = "w-5 h-5 flex-shrink-0 ml-3"
    
    switch (toast.type) {
      case 'success':
        return (
          <svg className={`${iconStyles} text-green-500`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
          </svg>
        )
      case 'error':
        return (
          <svg className={`${iconStyles} text-red-500`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        )
      case 'warning':
        return (
          <svg className={`${iconStyles} text-yellow-500`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        )
      case 'info':
        return (
          <svg className={`${iconStyles} text-blue-500`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        )
    }
  }

  return (
    <div className={getToastStyles()} dir="rtl">
      <div className="flex items-start">
        {getIcon()}
        
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-900 font-hebrew">
            {toast.title}
          </p>
          {toast.message && (
            <p className="text-sm text-gray-600 mt-1 font-hebrew">
              {toast.message}
            </p>
          )}
          
          {/* Actions */}
          {toast.actions && toast.actions.length > 0 && (
            <div className="mt-3 flex space-x-2 space-x-reverse">
              {toast.actions.map((action, index) => (
                <button
                  key={index}
                  onClick={action.action}
                  className={`text-xs px-3 py-1 rounded font-hebrew transition-colors ${
                    action.style === 'primary'
                      ? 'bg-blue-600 text-white hover:bg-blue-700'
                      : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                  }`}
                >
                  {action.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Dismiss button */}
        {toast.dismissible !== false && (
          <button
            onClick={handleDismiss}
            className="flex-shrink-0 ml-2 text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="סגור התראה"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>
    </div>
  )
}

// Toast provider component
export function ToastProvider({ children }: ToastProviderProps) {
  const [toasts, setToasts] = useState<Toast[]>([])

  const showToast = (toastData: Omit<Toast, 'id'>) => {
    const toast: Toast = {
      ...toastData,
      id: `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      duration: toastData.duration ?? 5000,
      dismissible: toastData.dismissible ?? true
    }

    setToasts(prev => [...prev, toast])
  }

  const showError = (error: AppError) => {
    const suggestedActions = errorHandlingService.getSuggestedActions(error)
    
    showToast({
      type: 'error',
      title: 'שגיאה במערכת',
      message: error.userMessage,
      duration: 8000, // Longer duration for errors
      actions: [
        ...suggestedActions.slice(0, 2).map(action => ({
          label: action,
          action: () => {
            if (action.includes('רענן')) {
              window.location.reload()
            } else if (action.includes('נסה שוב')) {
              dismissToast(error.type) // Custom retry logic can be added
            }
          },
          style: 'primary' as const
        })),
        ...(error.recoverable ? [{
          label: 'נסה שוב',
          action: () => window.location.reload(),
          style: 'secondary' as const
        }] : [])
      ]
    })
  }

  const showSuccess = (title: string, message?: string) => {
    showToast({
      type: 'success',
      title,
      message: message || '',
      duration: 4000
    })
  }

  const showWarning = (title: string, message?: string) => {
    showToast({
      type: 'warning',
      title,
      message: message || '',
      duration: 6000
    })
  }

  const showInfo = (title: string, message?: string) => {
    showToast({
      type: 'info',
      title,
      message: message || '',
      duration: 5000
    })
  }

  const dismissToast = (id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id))
  }

  return (
    <ToastContext.Provider value={{
      showToast,
      showError,
      showSuccess,
      showWarning,
      showInfo,
      dismissToast
    }}>
      {children}
      
      {/* Render toasts */}
      <div className="fixed bottom-4 right-4 z-50 space-y-2">
        {toasts.map(toast => (
          <ToastComponent
            key={toast.id}
            toast={toast}
            onDismiss={dismissToast}
          />
        ))}
      </div>
    </ToastContext.Provider>
  )
}


